import { z } from 'zod';

/**
 * Zod schemas for task-related entities
 * These provide runtime validation and type inference
 */

// Task schema: API/Prisma may return null or different enum strings
const taskStatusEnum = ['Open', 'Pending', 'In Progress', 'Completed'] as const;
const taskPriorityEnum = ['Low', 'Medium', 'High', 'Critical'] as const;
const taskTypeEnum = ['Maintenance', 'Leasing', 'Administrative'] as const;

const coerceEnum = <T extends readonly string[]>(allowed: T, fallback: T[number]) =>
  z.union([z.enum(allowed as unknown as [string, ...string[]]), z.string()]).transform(s => (allowed.includes(s as T[number]) ? (s as T[number]) : fallback));

export const taskSchema = z.object({
  id: z.string(),
  title: z.string().transform(t => (t?.trim() || 'Task')),
  description: z.preprocess((v) => (v == null ? '' : v), z.string()),
  status: coerceEnum(taskStatusEnum, 'Open'),
  priority: coerceEnum(taskPriorityEnum, 'Medium'),
  due: z.preprocess((v) => (v == null ? '' : typeof v === 'string' ? v : v instanceof Date ? v.toISOString().split('T')[0] : String(v)), z.string()),
  assigneeId: z.preprocess((v) => (v == null || v === '' ? null : v), z.string().nullable().optional()),
  assignee: z.preprocess((v) => (v == null ? '' : v), z.string()).optional(),
  propertyId: z.string().optional().nullable(),
  type: coerceEnum(taskTypeEnum, 'Administrative'),
}).passthrough();

export type Task = z.infer<typeof taskSchema>;
